import React, { useState, useEffect } from 'react';
import { Phone, Menu, X } from 'lucide-react';
import { BUSINESS } from '@/lib/businessData';

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const navLinks = [
    { label: 'Home', href: '/' },
    { label: 'Services', href: '/#services' },
    { label: 'About', href: '/about-mike' },
    { label: 'Service Areas', href: '/service-areas' },
    { label: 'Reviews', href: '/testimonials' },
    { label: 'Blog', href: '/blog' },
    { label: 'Contact', href: '/contact' },
    { label: 'Book Now', href: '/#booking' },
  ];

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-background/95 backdrop-blur-md border-b border-border' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <a href="/" className="flex items-center gap-2 group" aria-label="Mike's Garage Door Repair home">
              <div className="w-9 h-9 bg-primary flex items-center justify-center font-heading font-black text-background text-lg stencil">
                M
              </div>
              <div className="hidden sm:block">
                <div className="font-heading font-bold text-sm leading-none stencil">MIKE'S</div>
                <div className="font-mono text-[10px] text-muted-foreground leading-none mt-0.5">GARAGE DOOR REPAIR</div>
              </div>
            </a>

            {/* Desktop nav */}
            <nav className="hidden md:flex items-center gap-1" aria-label="Primary navigation">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  className="px-4 py-2 font-heading font-semibold text-xs uppercase tracking-wider text-foreground/80 hover:text-primary transition-colors"
                >
                  {link.label}
                </a>
              ))}
            </nav>

            {/* Call button */}
            <div className="flex items-center gap-2">
              <a
                href={`tel:${BUSINESS.phoneRaw}`}
                className="hidden sm:flex items-center gap-2 bg-primary text-background px-4 py-2 font-heading font-bold text-xs uppercase tracking-wider hover:amber-glow transition-all"
                aria-label={`Call ${BUSINESS.name} at ${BUSINESS.phone}`}
              >
                <Phone className="w-4 h-4" />
                {BUSINESS.phone}
              </a>
              <button
                onClick={() => setOpen(true)}
                className="md:hidden p-2 text-foreground"
                aria-label="Open navigation menu"
              >
                <Menu className="w-6 h-6" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Sliding panel nav — opens like a garage door */}
      {open && (
        <div className="fixed inset-0 z-[60] md:hidden">
          <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <div className="absolute top-0 left-0 right-0 bg-card border-b-2 border-primary animate-[accordion-down_0.3s_ease-out]">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <span className="font-heading font-bold text-sm uppercase stencil text-primary">Menu</span>
              <button onClick={() => setOpen(false)} aria-label="Close menu" className="p-2">
                <X className="w-6 h-6" />
              </button>
            </div>
            <nav className="flex flex-col" aria-label="Mobile navigation">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={() => setOpen(false)}
                  className="px-6 py-4 font-heading font-bold text-lg uppercase tracking-wider text-foreground border-b border-border hover:bg-secondary transition-colors"
                >
                  {link.label}
                </a>
              ))}
              <a
                href={`tel:${BUSINESS.phoneRaw}`}
                className="flex items-center justify-center gap-2 bg-primary text-background px-6 py-5 font-heading font-bold text-base uppercase tracking-wider"
              >
                <Phone className="w-5 h-5" />
                {BUSINESS.phone}
              </a>
            </nav>
          </div>
        </div>
      )}
    </>
  );
}